import { SearchFilters } from "@/types/podcast";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Filter, SortAsc, Layers, Activity, Check, Star, Youtube, MapPin } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { X as XIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { REGION_NAMES, REGION_FLAGS, SUPPORTED_REGIONS } from "@/utils/region-utils";

type RangeOption = {
  label: string;
  min?: number;
  max?: number;
};

interface RangeSelectorProps {
  label: string;
  minValue?: number;
  maxValue?: number;
  onChange: (min?: number, max?: number) => void;
  options: RangeOption[];
  step?: number;
  placeholderMin?: string;
  placeholderMax?: string;
  maxLimit?: number;
}

function RangeSelector({
  label,
  minValue,
  maxValue,
  onChange,
  options,
  step = 1,
  placeholderMin = "Min",
  placeholderMax = "Max",
  maxLimit
}: RangeSelectorProps) {
  const match = options.find(opt => opt.min === minValue && opt.max === maxValue);
  const [isCustom, setIsCustom] = useState(!match);

  useEffect(() => {
    // If external props change to match a preset, snap to it unless we are interacting?
    // Actually, usually beneficial to respect the match.
    // However, to allow "Custom" selection even when values match (to edit them), 
    // we need to be careful.
    // If values match, we can assume non-custom, UNLESS user explicitly set custom.
    // But here we'll simplify: if it matches, it shows the preset. If user wants to edit, they select Custom.
    if (match) {
        setIsCustom(false);
    } else {
        setIsCustom(true);
    }
  }, [minValue, maxValue, match]); // Depend on match result

  const selection = isCustom ? "custom" : (match ? JSON.stringify({ min: match.min, max: match.max }) : "custom");

  return (
      <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <div className="space-y-2">
              <Select
                  value={selection}
                  onValueChange={(val) => {
                      if (val === "custom") {
                           setIsCustom(true);
                           // Don't change values, just switch mode to show inputs
                      } else {
                           setIsCustom(false);
                           const { min, max } = JSON.parse(val);
                           onChange(min, max);
                      }
                  }}
              >
                  <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select range..." />
                  </SelectTrigger>
                  <SelectContent>
                      <SelectItem value={JSON.stringify({ min: undefined, max: undefined })}>Any</SelectItem>
                      {options.map((opt, i) => (
                          <SelectItem key={i} value={JSON.stringify({ min: opt.min, max: opt.max })}>
                              {opt.label}
                          </SelectItem>
                      ))}
                      <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
              </Select>
              
              {isCustom && (
                  <div className="flex items-center gap-2 animate-in fade-in slide-in-from-top-1">
                      <Input
                          type="number"
                          min="0"
                          max={maxLimit}
                          step={step}
                          placeholder={placeholderMin}
                          value={minValue !== undefined ? minValue : ''}
                          onChange={(e) => {
                              const val = e.target.value ? parseFloat(e.target.value) : undefined;
                              onChange(val, maxValue);
                          }}
                          className="h-8 text-sm"
                      />
                      <span className="text-muted-foreground">-</span>
                      <Input
                          type="number"
                          min="0"
                          max={maxLimit}
                          step={step}
                          placeholder={placeholderMax}
                          value={maxValue !== undefined ? maxValue : ''}
                          onChange={(e) => {
                              const val = e.target.value ? parseFloat(e.target.value) : undefined;
                              onChange(minValue, val);
                          }}
                          className="h-8 text-sm"
                      />
                   </div>
              )}
          </div>
      </div>
  );
}

interface FiltersContentProps {
  filters: SearchFilters;
  onFilterChange: (filters: SearchFilters) => void;
  episodeKeywords?: string[];
  onEpisodeKeywordsChange?: (keywords: string[]) => void;
  sortField?: 'relevance' | 'popularity' | 'recent' | 'episodeCount' | 'listeners' | 'rating' | 'reviews' | 'date' | 'youtubeSubscribers';
  onSortFieldChange?: (field: 'relevance' | 'popularity' | 'recent' | 'episodeCount' | 'listeners' | 'rating' | 'reviews' | 'date' | 'youtubeSubscribers') => void;
  sortOrder?: 'asc' | 'desc';
  onSortOrderChange?: (order: 'asc' | 'desc') => void;
}

export function FiltersContent({
    filters,
    onFilterChange,
    episodeKeywords = [],
    onEpisodeKeywordsChange,
    sortField = 'popularity',
    onSortFieldChange,
    sortOrder = 'desc',
    onSortOrderChange
}: FiltersContentProps) {
    const [keywordInput, setKeywordInput] = useState("");

    const handleAddKeyword = () => {
        if (!keywordInput.trim() || !onEpisodeKeywordsChange) return;
        if (!episodeKeywords.includes(keywordInput.trim())) {
            onEpisodeKeywordsChange([...episodeKeywords, keywordInput.trim()]);
        }
        setKeywordInput("");
    };

    const handleRemoveKeyword = (e: React.MouseEvent, keyword: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (!onEpisodeKeywordsChange) return;
        onEpisodeKeywordsChange(episodeKeywords.filter(k => k !== keyword));
    };

    const handleClearAllKeywords = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!onEpisodeKeywordsChange) return;
        onEpisodeKeywordsChange([]);
    };

    const updateFilter = (key: keyof SearchFilters, value: any) => {
        onFilterChange({ ...filters, [key]: value });
    };

    return (
        <div className="space-y-1">
            {/* Header */}
            <div className="mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                    <Filter className="size-4" />
                    Filters & Sort
                </h3>
            </div>

            <Accordion type="multiple" defaultValue={['keywords', 'sort', 'reviews', 'requirements', 'episodes', 'activity']} className="w-full">
            
            {/* Episode Keywords Section */}
            <AccordionItem value="keywords" className="border-b-0" data-tour="episode-keywords">
                <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                    <span className="flex items-center gap-2"><Layers className="size-4 text-muted-foreground" /> Episode Keywords</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                    <div className="space-y-3 px-1">
                        <div className="flex gap-2">
                            <Input 
                                placeholder="Add keyword..." 
                                value={keywordInput}
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddKeyword();
                                    }
                                }}
                                className="h-8 text-sm"
                            />
                            <Button variant="secondary" size="sm" onClick={handleAddKeyword} className="h-8 px-2">
                                Add
                            </Button>
                        </div>
                        {episodeKeywords.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-muted-foreground font-medium">Active Keywords:</span>
                                    <span 
                                        className="text-[10px] text-destructive cursor-pointer hover:underline font-medium"
                                        onClick={handleClearAllKeywords}
                                    >
                                        Clear All
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {episodeKeywords.map((kw, i) => (
                                        <div key={i} className="inline-flex items-center rounded-md border border-transparent bg-secondary px-1.5 py-0.5 text-xs font-normal text-secondary-foreground hover:bg-secondary/80 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                                            {kw}
                                            <button
                                                type="button"
                                                className="ml-1 rounded-full outline-none ring-offset-background hover:bg-muted focus:ring-2 focus:ring-ring focus:ring-offset-2 pointer-events-auto z-50 mb-0.5"
                                                onClick={(e) => handleRemoveKeyword(e, kw)}
                                            >
                                                <XIcon className="h-3 w-3 opacity-50 hover:opacity-100" />
                                                <span className="sr-only">Remove {kw}</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                         <p className="text-[10px] text-muted-foreground">
                            Filter podcasts that have episodes matching these specific keywords.
                        </p>
                    </div>
                </AccordionContent>
            </AccordionItem>

            {/* Sort Section */}
            <AccordionItem value="sort" className="border-b-0">
                <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                    <span className="flex items-center gap-2"><SortAsc className="size-4 text-muted-foreground" /> Sort Options</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                    <div className="space-y-4 px-1">
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Sort Field</Label>
                            <Select
                                value={sortField}
                                onValueChange={(v) => onSortFieldChange?.(v as any)}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="relevance">Relevance</SelectItem>
                                    <SelectItem value="popularity">Popularity</SelectItem>
                                    <SelectItem value="listeners">Listeners</SelectItem>
                                    <SelectItem value="recent">Recent</SelectItem>
                                    <SelectItem value="date">Date</SelectItem>
                                    <SelectItem value="episodeCount">Episode Count</SelectItem>
                                    <SelectItem value="rating">Rating</SelectItem>
                                    <SelectItem value="reviews">Reviews</SelectItem>
                                    <SelectItem value="youtubeSubscribers">YouTube Subscribers</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Order</Label>
                            <Select
                                value={sortOrder}
                                onValueChange={(v) => onSortOrderChange?.(v as 'asc' | 'desc')}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="desc">Descending</SelectItem>
                                    <SelectItem value="asc">Ascending</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </AccordionContent>
            </AccordionItem>

            {/* Ratings & Reviews Section */}
            <AccordionItem value="reviews" className="border-b-0">
                <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                     <span className="flex items-center gap-2"><Star className="size-4 text-muted-foreground" /> Ratings & Reviews</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                     <div className="space-y-4 px-1">
                        <RangeSelector
                            label="Rating Range (0-5)"
                            minValue={filters.minRating}
                            maxValue={filters.maxRating}
                            onChange={(min, max) => onFilterChange({ ...filters, minRating: min, maxRating: max })}
                            options={[
                                { label: "4.5 stars & up", min: 4.5 },
                                { label: "4.0 stars & up", min: 4.0 },
                                { label: "3.5 stars & up", min: 3.5 },
                                { label: "3.0 - 5.0 stars", min: 3.0, max: 5.0 }
                            ]}
                            step={0.1}
                            maxLimit={5}
                        />

                        <RangeSelector
                            label="Review Count"
                            minValue={filters.minRatingCount}
                            maxValue={filters.maxRatingCount}
                            onChange={(min, max) => onFilterChange({ ...filters, minRatingCount: min, maxRatingCount: max })}
                            options={[
                                { label: "< 50 Reviews", max: 50 },
                                { label: "50 - 100 Reviews", min: 50, max: 100 },
                                { label: "100 - 500 Reviews", min: 100, max: 500 },
                                { label: "500+ Reviews", min: 500 }
                            ]}
                        />
                     </div>
                </AccordionContent>
            </AccordionItem>

            {/* Social Media Section */}
            <AccordionItem value="social" className="border-b-0">
                <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                     <span className="flex items-center gap-2"><Youtube className="size-4 text-muted-foreground" /> Social Media</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                     <div className="space-y-4 px-1">
                        <RangeSelector
                            label="YouTube Subscribers"
                            minValue={filters.minYoutubeSubscriberCount}
                            maxValue={(filters as any).maxSubscriberCount}
                            onChange={(min, max) => onFilterChange({ ...filters, minYoutubeSubscriberCount: min, maxSubscriberCount: max } as any)}
                            options={[
                                { label: "1k+ Subscribers", min: 1000 },
                                { label: "10k+ Subscribers", min: 10000 },
                                { label: "100k+ Subscribers", min: 100000 },
                                { label: "1M+ Subscribers", min: 1000000 }
                            ]}
                        />
                     </div>
                </AccordionContent>
            </AccordionItem>

            {/* Primary Market / Region Section */}
            <AccordionItem value="region" className="border-b-0">
                <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                     <span className="flex items-center gap-2"><MapPin className="size-4 text-muted-foreground" /> Region</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                     <div className="space-y-3 px-1">
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Primary Market</Label>
                            <Select
                                value={filters.primaryMarket || '_any'}
                                onValueChange={(val) => updateFilter('primaryMarket', val === '_any' ? undefined : val)}
                            >
                                <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Any region" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_any">Any Region</SelectItem>
                                    {SUPPORTED_REGIONS.map((code) => (
                                        <SelectItem key={code} value={code}>
                                            {REGION_FLAGS[code]} {REGION_NAMES[code]} ({code.toUpperCase()})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            Filter by the region where the podcast has the most reviews.
                        </p>
                     </div>
                </AccordionContent>
            </AccordionItem>

            {/* Requirements Section */}
            <AccordionItem value="requirements" className="border-b-0">
                <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                     <span className="flex items-center gap-2"><Check className="size-4 text-muted-foreground" /> Requirements</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                     <div className="space-y-3 px-1">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="hasEmail"
                                checked={!!filters.hasEmail}
                                onCheckedChange={(checked) => updateFilter('hasEmail', checked)}
                            />
                            <Label htmlFor="hasEmail" className="text-sm font-normal cursor-pointer text-slate-700">
                                Has Email Address
                            </Label>
                        </div>

                    </div>
                </AccordionContent>
            </AccordionItem>

            {/* Episode Count */}
            <AccordionItem value="episodes" className="border-b-0">
                 <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                     <span className="flex items-center gap-2"><Layers className="size-4 text-muted-foreground" /> Episode Count</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                    <div className="space-y-4 px-1">
                        <RangeSelector
                            label="Episode Count"
                            minValue={filters.minEpisodes}
                            maxValue={filters.maxEpisodes}
                            onChange={(min, max) => onFilterChange({ ...filters, minEpisodes: min, maxEpisodes: max })}
                            options={[
                                { label: "< 10 Episodes", max: 10 },
                                { label: "10 - 50 Episodes", min: 10, max: 50 },
                                { label: "50 - 100 Episodes", min: 50, max: 100 },
                                { label: "100+ Episodes", min: 100 }
                            ]}
                        />
                    </div>
                </AccordionContent>
             </AccordionItem>

            {/* Activity Filter */}
             <AccordionItem value="activity" className="border-b-0">
                <AccordionTrigger className="hover:no-underline py-2 text-sm font-medium">
                    <span className="flex items-center gap-2"><Activity className="size-4 text-muted-foreground" /> Activity</span>
                </AccordionTrigger>
                <AccordionContent className="pt-1 pb-4">
                    <div className="space-y-2 px-1">
                        <Label className="text-xs text-muted-foreground">Active Within (Days)</Label>
                        <Input
                            type="number"
                            placeholder="e.g. 90"
                            value={filters.activeWithinDays || ''}
                            onChange={(e) => updateFilter('activeWithinDays', e.target.value ? parseInt(e.target.value) : undefined)}
                            className="h-9"
                        />
                    </div>
                </AccordionContent>
            </AccordionItem>
            </Accordion>
        </div>
    );
}

export function FiltersToolbar({
    filters,
    onFilterChange,
    sortField = 'popularity',
    onSortFieldChange,
    sortOrder = 'desc',
    onSortOrderChange
}: FiltersContentProps) {
    const updateFilter = (key: keyof SearchFilters, value: any) => {
        onFilterChange({ ...filters, [key]: value });
    };

    const hasRequirements = !!filters.hasEmail;
    const hasEpisodes = !!filters.minEpisodes;
    const hasActivity = !!filters.activeWithinDays;

    return (
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {/* Sort Popover */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 border-dashed gap-1 text-xs">
                        <SortAsc className="h-3.5 w-3.5" />
                        Sort
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-4" align="start">
                    <div className="space-y-4">
                        <h4 className="font-medium text-sm leading-none">Sort Options</h4>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Field</Label>
                                <Select value={sortField} onValueChange={(v) => onSortFieldChange?.(v as any)}>
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="relevance">Relevance</SelectItem>
                                        <SelectItem value="popularity">Popularity</SelectItem>
                                        <SelectItem value="listeners">Listeners</SelectItem>
                                        <SelectItem value="recent">Recent</SelectItem>
                                        <SelectItem value="episodeCount">Episodes</SelectItem>
                                        <SelectItem value="youtubeSubscribers">YouTube Subscribers</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Order</Label>
                                <Select value={sortOrder} onValueChange={(v) => onSortOrderChange?.(v as 'asc' | 'desc')}>
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="desc">Descending</SelectItem>
                                        <SelectItem value="asc">Ascending</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            <div className="h-4 w-px bg-border mx-1" />

            {/* Requirements Popover */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={cn(
                            "h-8 border-dashed gap-1 text-xs",
                            hasRequirements && "bg-secondary border-secondary-foreground/20 text-secondary-foreground border-solid"
                        )}
                    >
                        <Check className="h-3.5 w-3.5" />
                        Requirements
                        {hasRequirements && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-primary" />}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[240px] p-4" align="start">
                    <div className="space-y-4">
                        <h4 className="font-medium text-sm leading-none">Requirements</h4>
                        <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="pop-hasEmail"
                                    checked={!!filters.hasEmail}
                                    onCheckedChange={(checked) => updateFilter('hasEmail', checked)}
                                />
                                <Label htmlFor="pop-hasEmail" className="text-sm font-normal cursor-pointer">
                                    Has Email
                                </Label>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            {/* Episodes Popover */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={cn(
                            "h-8 border-dashed gap-1 text-xs",
                            hasEpisodes && "bg-secondary border-secondary-foreground/20 text-secondary-foreground border-solid"
                        )}
                    >
                        <Layers className="h-3.5 w-3.5" />
                        Episodes
                        {hasEpisodes && <span className="ml-1 text-[10px] font-semibold">{filters.minEpisodes}+</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-4" align="start">
                     <div className="space-y-4">
                        <div className="flex justify-between items-center">
                             <h4 className="font-medium text-sm leading-none">Min Episodes</h4>
                             <span className="text-xs text-muted-foreground font-mono">{filters.minEpisodes || 0}</span>
                        </div>
                        <Slider
                            value={[filters.minEpisodes || 0]}
                            max={200}
                            step={10}
                            onValueChange={(vals) => updateFilter('minEpisodes', vals[0])}
                            className="w-full"
                        />
                         <p className="text-[10px] text-muted-foreground">
                            Filter podcasts with at least this many episodes.
                        </p>
                    </div>
                </PopoverContent>
            </Popover>

            {/* YouTube Subscribers Popover */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={cn(
                            "h-8 border-dashed gap-1 text-xs",
                            (filters as any).minYoutubeSubscriberCount && "bg-red-50 border-red-200 text-red-600 border-solid"
                        )}
                    >
                        <Youtube className="h-3.5 w-3.5" />
                        Subscribers
                        {(filters as any).minYoutubeSubscriberCount && <span className="ml-1 text-[10px] font-semibold">{((filters as any).minYoutubeSubscriberCount / 1000)}k+</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-4" align="start">
                     <div className="space-y-4">
                        <div className="flex justify-between items-center">
                             <h4 className="font-medium text-sm leading-none flex items-center gap-2">
                                <Youtube className="size-3.5 text-red-600" />
                                Min Subscribers
                             </h4>
                        </div>
                        <div className="flex flex-col gap-2">
                            {[
                                { label: 'Any', value: undefined },
                                { label: '1k+', value: 1000 },
                                { label: '5k+', value: 5000 },
                                { label: '10k+', value: 10000 },
                                { label: '50k+', value: 50000 },
                                { label: '100k+', value: 100000 },
                                { label: '500k+', value: 500000 },
                                { label: '1M+', value: 1000000 },
                            ].map((option) => (
                                <div key={option.label} className="flex items-center space-x-2">
                                     <Checkbox 
                                        id={`yt-sub-${option.label}`}
                                        checked={(filters as any).minYoutubeSubscriberCount === option.value}
                                        onCheckedChange={(checked) => {
                                            if (checked) {
                                                updateFilter('minYoutubeSubscriberCount' as any, option.value);
                                            } else {
                                                if ((filters as any).minYoutubeSubscriberCount === option.value) {
                                                    updateFilter('minYoutubeSubscriberCount' as any, undefined); 
                                                }
                                            }
                                        }}
                                     />
                                     <Label htmlFor={`yt-sub-${option.label}`} className="text-sm font-normal cursor-pointer">
                                        {option.label}
                                     </Label>
                                </div>
                            ))}
                        </div>
                         <p className="text-[10px] text-muted-foreground">
                            Filter podcasts with a verified YouTube channel and minimum subscriber count.
                        </p>
                    </div>
                </PopoverContent>
            </Popover>

             {/* Activity Popover */}
             <Popover>
                <PopoverTrigger asChild>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={cn(
                            "h-8 border-dashed gap-1 text-xs",
                            hasActivity && "bg-secondary border-secondary-foreground/20 text-secondary-foreground border-solid"
                        )}
                    >
                        <Activity className="h-3.5 w-3.5" />
                        Activity
                        {hasActivity && <span className="ml-1 text-[10px] font-semibold">&lt;{filters.activeWithinDays}d</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-4" align="start">
                     <div className="space-y-3">
                        <h4 className="font-medium text-sm leading-none">Last Active</h4>
                         <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">Within Days</Label>
                            <Input
                                type="number"
                                placeholder="e.g. 90"
                                value={filters.activeWithinDays || ''}
                                onChange={(e) => updateFilter('activeWithinDays', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="h-8 text-sm"
                            />
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            Published an episode within this many days.
                        </p>
                    </div>
                </PopoverContent>
            </Popover>

            {/* Region Popover */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={cn(
                            "h-8 border-dashed gap-1 text-xs",
                            filters.primaryMarket && "bg-purple-50 border-purple-200 text-purple-600 border-solid"
                        )}
                    >
                        <MapPin className="h-3.5 w-3.5" />
                        Region
                        {filters.primaryMarket && <span className="ml-1 text-[10px] font-semibold">{REGION_FLAGS[filters.primaryMarket]} {filters.primaryMarket.toUpperCase()}</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-4" align="start">
                     <div className="space-y-3">
                        <h4 className="font-medium text-sm leading-none flex items-center gap-2">
                            <MapPin className="size-3.5 text-purple-600" />
                            Primary Market
                        </h4>
                        <div className="flex flex-col gap-2">
                            {[
                                { label: 'Any', value: undefined },
                                ...SUPPORTED_REGIONS.map(code => ({ label: `${REGION_FLAGS[code]} ${REGION_NAMES[code]} (${code.toUpperCase()})`, value: code }))
                            ].map((option) => (
                                <div key={option.label} className="flex items-center space-x-2">
                                     <Checkbox 
                                        id={`region-${option.label}`}
                                        checked={filters.primaryMarket === option.value}
                                        onCheckedChange={(checked) => {
                                            if (checked) {
                                                updateFilter('primaryMarket', option.value);
                                            } else {
                                                if (filters.primaryMarket === option.value) {
                                                    updateFilter('primaryMarket', undefined);
                                                }
                                            }
                                        }}
                                     />
                                     <Label htmlFor={`region-${option.label}`} className="text-sm font-normal cursor-pointer">
                                        {option.label}
                                     </Label>
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            Filter by the region with the most reviews.
                        </p>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}

export function FiltersBar(props: FiltersContentProps) {
  return (
    <aside className="w-72 bg-white border-r border-border flex flex-col h-full overflow-y-auto hidden lg:flex flex-shrink-0" data-tour="filters-sidebar">
      <div className="p-4">
        <FiltersContent {...props} />
      </div>
    </aside>
  );
}
